/**
 * CV types for ATS.
 */

import type { CVJobRejectionReason, CVJobSource } from './job';

// --- JSON Resume Types (https://jsonresume.org/schema) ---

export interface JsonResumeLocation {
  address?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
  region?: string;
}

export interface JsonResumeProfile {
  network?: string;
  username?: string;
  url?: string;
}

export interface JsonResumeCustom {
  gender?: string;
  dob?: string;
  startWorkingYear?: number;
}

export interface JsonResumeBasics {
  name?: string;
  label?: string;
  image?: string;
  email?: string;
  phone?: string;
  url?: string;
  summary?: string;
  location?: JsonResumeLocation;
  profiles?: JsonResumeProfile[];
  custom?: JsonResumeCustom;
}

export interface JsonResumeWork {
  name?: string;
  position?: string;
  url?: string;
  startDate?: string;
  endDate?: string;
  summary?: string;
  highlights?: string[];
  // Additional fields from backend (not in JSON Resume standard)
  company?: string; // Backend uses 'company' instead of 'name'
  location?: string;
  description?: string;
}

export interface JsonResumeEducation {
  institution?: string;
  url?: string;
  area?: string;
  studyType?: string;
  startDate?: string;
  endDate?: string;
  score?: string;
  courses?: string[];
}

export interface JsonResumeSkill {
  name?: string;
  level?: string;
  keywords?: string[];
}

export interface JsonResumeLanguage {
  language?: string;
  fluency?: string;
}

export interface JsonResumeAward {
  title?: string;
  date?: string;
  awarder?: string;
  summary?: string;
}

export interface JsonResumeCertificate {
  name?: string;
  date?: string;
  issuer?: string;
  url?: string;
}

export interface JsonResumeVolunteer {
  organization?: string;
  position?: string;
  startDate?: string;
  endDate?: string;
  summary?: string;
}

export interface JsonResumeInterest {
  name?: string;
  keywords?: string[];
}

export interface JsonResumeReference {
  name?: string;
  reference?: string;
}

export interface JsonResume {
  basics?: JsonResumeBasics;
  work?: JsonResumeWork[];
  education?: JsonResumeEducation[];
  skills?: JsonResumeSkill[];
  languages?: JsonResumeLanguage[];
  awards?: JsonResumeAward[];
  certificates?: JsonResumeCertificate[];
  volunteer?: JsonResumeVolunteer[];
  interests?: JsonResumeInterest[];
  references?: JsonResumeReference[];
}

// --- CV Types ---

export interface CVPool {
  id: string;
  name: string;
}

export interface CVPosition {
  id: string;
  name: string;
}

export interface CVJobRound {
  id: string;
  name: string;
  round_type: string | null;
}

export interface CVJob {
  id: string;
  code: string;
  position_name: string;
  campaign_name: string;
  pipeline_id: string | null;
  pipeline_name: string | null;
  round: CVJobRound | null;
  is_rejected: boolean;
  rejection_reason: CVJobRejectionReason | null;
  rejection_reason_text: string | null;
  source: CVJobSource | null;
  applied_at: string | null;
  onboard_date: string | null;
}

export interface CVAttachment {
  id: string;
  file_url: string;
  file_name: string;
  file_type: string;
  file_size: number | null;
}

export interface CVTag {
  id: string;
  name: string;
  slug: string;
}

export type CVProcessingStatus = 'pending' | 'processing' | 'processed' | 'error';

export interface CV {
  id: string;
  name: string;
  phone?: string | null;
  attachments: CVAttachment[];
  processing_status: CVProcessingStatus;
  parsed_data?: JsonResume;
  pools: CVPool[];
  positions: CVPosition[];
  jobs: CVJob[];
  tags: CVTag[];
  created_at: string;
}

// --- CV Duplicate Types ---

export interface CVDuplicateCVInfo {
  id: string;
  name: string;
}

export interface CVDuplicate {
  id: string;
  cv_a: CVDuplicateCVInfo;
  cv_b: CVDuplicateCVInfo;
  match_type: 'email' | 'phone' | 'both';
  match_value: string;
  status: 'pending' | 'merged' | 'dismissed';
  created_at: string;
}

export interface CVDuplicateListResponse {
  items: CVDuplicate[];
  total: number;
}

export interface CVMergeResponse {
  surviving_cv_id: string;
  deleted_cv_id: string;
  attachments_transferred: number;
  jobs_transferred: number;
}

export interface CVListResponse {
  items: CV[];
  total: number;
}

export const PROCESSING_STATUS_CONFIG: Record<
  CVProcessingStatus,
  { label: string; color: 'gray' | 'blue' | 'green' | 'red' }
> = {
  pending: { label: 'Pending', color: 'gray' },
  processing: { label: 'Processing', color: 'blue' },
  processed: { label: 'Processed', color: 'green' },
  error: { label: 'Error', color: 'red' },
};
